

















































































































































import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import BrandNameEditDialog from '@/views/SuperAdminViews/super-admin-view/BrandNameEditDialog.vue';
import SettingsRolesSelect from '@/views/SettingsViews/SettingsRolesSelect.vue';
import VStatus from '@/components/VStatus.vue';
import VCount from '@/components/VCount.vue';
import {
  deleteAccount,
  deleteOperator,
  getSuperAdminAccounts,
  updateAccountBrandName,
  updateAccountRole
} from '@/api/SuperAdmin';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { snakeCase } from 'lodash';
import { DataTableHeader } from 'vuetify';
import { AxiosError } from 'axios';
import {
  AccountsParams,
  ERole,
  ESuperAdminAccountsStatus,
  TAccountWithOperators,
  TOperatorAccount
} from '@/api/schema';

const defaultOptions = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['created_at'],
  sortDesc: [false]
};

export const statusMap = {
  [ESuperAdminAccountsStatus.NEW]: {
    label: 'New',
    type: 'additional-new'
  },
  [ESuperAdminAccountsStatus.INTEGRATION]: {
    label: 'Integration',
    type: 'system-info'
  },
  [ESuperAdminAccountsStatus.INTEGRATION_STAGE]: {
    label: 'Integration Stage',
    type: 'additional-progress'
  },
  [ESuperAdminAccountsStatus.PROD]: {
    label: 'Prod',
    type: 'system-success'
  }
};

export default {
  name: 'SuperAdminAccountsView',
  components: {
    ConfirmationDialog,
    BrandNameEditDialog,
    VStatus,
    SettingsRolesSelect,
    VCount
  },
  data(): any {
    return {
      statusMap,
      accountsList: [] as TAccountWithOperators[],
      isLoading: false,
      total: 0,
      options: { ...defaultOptions },
      editDialog: false,
      processingStatus: false,
      deleteDialog: false,
      processingData: null,
      searchPhrase: '',
      headers: [
        {
          text: '',
          value: 'data-table-expand',
          class: 'no-divider-header-column',
          cellClass: 'px-2'
        },
        {
          text: 'Prod ID',
          value: 'operatorId',
          sortable: false
        },
        {
          text: 'Brand Name',
          value: 'brandName',
          sortable: false
        },
        {
          text: 'Login',
          value: 'login',
          sortable: false
        },
        {
          text: 'Status',
          value: 'status',
          sortable: true
        },
        {
          text: 'Role',
          value: 'role',
          sortable: false
        },
        {
          text: 'Created At',
          value: 'createdAt',
          sortable: true
        },
        {
          text: 'Action',
          value: 'actions',
          sortable: false
        }
      ] as DataTableHeader[]
    };
  },
  computed: {
    getPageData(): AccountsParams {
      return {
        page: this.options.page - 1,
        size: this.options.itemsPerPage,
        'sort-direction': this.options.sortDesc?.[0] ? 'DESC' : 'ASC',
        'sort-property': snakeCase(this.options.sortBy[0]) // Need fixed from backend (need snake case format created_at)
      };
    }
  },
  methods: {
    rowClickHandler(
      account: TAccountWithOperators,
      operator: TOperatorAccount | null = null
    ): void {
      if (!operator && !account.operators.length) return;

      this.$router.push({
        name: 'account-operator',
        params: {
          id: String(account.id),
          operatorId: operator ? operator.id : account.operators[0].id
        }
      });
    },
    updatePageOptionsHandler(): void {
      this.loadAccounts();
    },
    handlerClearSearchAccountPhrase(): void {
      this.searchPhrase = '';
      if (this.options.page === 1) {
        this.loadAccounts();
      } else {
        this.options.page = 1;
      }
    },
    handlerSearchAccountPhrase(): void {
      if (this.options.page === 1) {
        this.loadAccounts();
      } else {
        this.options.page = 1;
      }
    },
    async loadAccounts(): Promise<void> {
      try {
        this.isLoading = true;

        const res = await getSuperAdminAccounts({
          ...this.getPageData,
          ...(this.searchPhrase ? { query: this.searchPhrase } : {})
        });

        this.accountsList = res.content;
        this.total = res.totalElements;
      } catch (error) {
        errorToastMessage(error);
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    showEditDialog(item: TAccountWithOperators): void {
      this.editDialog = true;
      this.processingData = item;
    },
    closeEditDialog(): void {
      this.editDialog = false;
      this.processingData = null;
    },
    editHandler(id: number, name: string): void {
      this.processingStatus = true;
      updateAccountBrandName(id, name)
        .then(() => {
          this.editDialog = false;
          this.$toast.success('Brand name updated');
          this.loadAccounts();
        })
        .catch(errorToastMessage)
        .finally(() => (this.processingStatus = false));
    },
    showDeleteAccountDialog(account: TAccountWithOperators): void {
      this.deleteDialog = true;
      this.processingData = {
        header: `Are you sure you want to delete account: "${
          account.brandName || account.login
        }"?`,
        okText: 'Delete',
        account: account
      };
    },
    showDeleteOperatorDialog(
      operator: TOperatorAccount,
      accountId: number
    ): void {
      this.deleteDialog = true;
      this.processingData = {
        header: `Are you sure you want to delete operator: "${operator.name}"?`,
        okText: 'Delete',
        operator: {
          ...operator,
          accountId: accountId
        }
      };
    },
    closeDeleteDialog(): void {
      this.deleteDialog = false;
      this.processingData = null;
    },
    deleteHandler(): void {
      this.processingStatus = true;
      let deleteItemName = '';
      let deletePromise;

      if (this.processingData.account) {
        const { brandName, login, id } = this.processingData.account;
        deleteItemName = brandName || login;

        deletePromise = deleteAccount(id);
      } else if (this.processingData.operator) {
        const { name, id, accountId } = this.processingData.operator;
        deleteItemName = name;

        deletePromise = deleteOperator(accountId, id);
      }

      deletePromise
        .then(() => {
          this.$toast.success(`${deleteItemName} was deleted successfully`);
          this.deleteDialog = false;
          this.loadAccounts();
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);
          console.error(error);
        })
        .finally(() => {
          this.processingStatus = false;
        });
    },
    getAccountRole(account: TAccountWithOperators): ERole {
      return account.copyStakeAccount
        ? ERole.ROLE_COPY_STAKE_ACCOUNT
        : ERole.ROLE_ACCOUNT;
    },
    changeAccountRoleHandler(
      role: ERole,
      account: TAccountWithOperators
    ): void {
      const isCopyStakeRole = role === ERole.ROLE_COPY_STAKE_ACCOUNT;

      updateAccountRole(account.id, isCopyStakeRole)
        .then(() => {
          this.$toast.success('Account role changed.');
        })
        .catch((error: AxiosError): void => {
          errorToastMessage(error);
        });
    }
  }
};
